// Polyfill to prevent crashes when Storage is unavailable (Safari ITP, private mode, cross-site iframes)
// Wraps localStorage/sessionStorage methods with try/catch and falls back to in-memory storage.
(function () {
  if (typeof window === 'undefined') return;

  const createMemoryStore = () => {
    const map = new Map<string, string>();
    return {
      getItem: (key: string) => (map.has(key) ? map.get(key)! : null),
      setItem: (key: string, value: string) => {
        map.set(key, String(value));
      },
      removeItem: (key: string) => {
        map.delete(key);
      },
      clear: () => {
        map.clear();
      },
      key: (index: number) => Array.from(map.keys())[index] ?? null,
    } as Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear' | 'key'>;
  };

  const patchStorage = (store: Storage, name: 'localStorage' | 'sessionStorage') => {
    const memory = createMemoryStore();

    const wrap = <A extends any[], R>(fn: (...args: A) => R, fallback: (...args: A) => R) => {
      return (...args: A) => {
        try {
          return fn.apply(store, args);
        } catch (e) {
          // console.warn(`${name} access failed, using memory fallback`, e);
          return fallback(...args);
        }
      };
    };

    // Quick probe - some Safari contexts throw on any access
    try {
      const testKey = `__storage_test__${Math.random()}`;
      store.setItem(testKey, '1');
      store.removeItem(testKey);
    } catch (e) {
      console.warn(`${name} is blocked; falling back to in-memory.`);
      store.getItem = memory.getItem as any;
      store.setItem = memory.setItem as any;
      store.removeItem = memory.removeItem as any;
      store.clear = memory.clear as any;
      store.key = memory.key as any;
      return;
    }

    // Even if probe succeeded, wrap to guard intermittent throws
    store.getItem = wrap(store.getItem.bind(store), memory.getItem as any) as any;
    store.setItem = wrap(store.setItem.bind(store), memory.setItem as any) as any;
    store.removeItem = wrap(store.removeItem.bind(store), memory.removeItem as any) as any;
    store.clear = wrap(store.clear.bind(store), memory.clear as any) as any;
    store.key = wrap(store.key.bind(store), memory.key as any) as any;
  };

  try { patchStorage(window.localStorage, 'localStorage'); } catch {}
  try { patchStorage(window.sessionStorage, 'sessionStorage'); } catch {}

  // Phase 2: Cookie backup for Supabase auth keys on iOS PWA
  // CRITICAL: Defer cookie operations until DOM is ready to prevent iOS crashes
  const initCookieBackup = () => {
    try {
      const authKeyPrefix = 'sb-';
      const originalSetItem = window.localStorage.setItem.bind(window.localStorage);
      const originalGetItem = window.localStorage.getItem.bind(window.localStorage);

      window.localStorage.setItem = function(key: string, value: string) {
        originalSetItem(key, value);
        
        // Backup auth keys to cookies
        if (key.startsWith(authKeyPrefix)) {
          try {
            const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
            document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; expires=${expires}; path=/; secure; samesite=strict`;
          } catch (e) {
            console.warn('Cookie backup failed for', key);
          }
        }
      };

      window.localStorage.getItem = function(key: string) {
        let value = originalGetItem(key);
        
        // If localStorage returns null but we have a cookie backup, restore it
        if (!value && key.startsWith(authKeyPrefix)) {
          try {
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
              const [cookieKey, cookieValue] = cookie.trim().split('=');
              if (decodeURIComponent(cookieKey) === key) {
                value = decodeURIComponent(cookieValue);
                // Restore to localStorage
                try {
                  originalSetItem(key, value);
                } catch (e) {
                  console.warn('Could not restore from cookie to localStorage');
                }
                break;
              }
            }
          } catch (e) {
            // Silently fail if cookies aren't ready yet
          }
        }
        
        return value;
      };
    } catch (e) {
      console.warn('Failed to set up cookie backup for auth keys', e);
    }
  };

  // Only init cookie backup after DOM is ready (prevents iOS crash)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCookieBackup);
  } else {
    initCookieBackup();
  }
})();
